import React, { FC } from 'react';
import { useNavigate } from 'react-router';
import Image from 'components/image/Image';
import useLocales from 'hooks/useLocales';
import Button from 'components/Button/Index';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { IIndustry } from 'core/interface/companies';
import {
  getIndustryDisplayName,
  normalizeIndustry
} from 'utils/industry';

import './Index.scss';
import { companySizeChoices } from 'core/constants/aboutMePopUpConstants';

interface ICompanyCardProps {
  id: number;
  name: string;
  logo: string;
  heroImage?: string;
  industry?: IIndustry | null;
  size?: number;
  location?: string;
  number_of_jobs?: number;
  currency?: string;
  slug?: string;
  hasCareerPage?: boolean;
}

const CompanyCard: FC<ICompanyCardProps> = ({
  id,
  name,
  logo,
  industry,
  size,
  location,
  number_of_jobs,
  slug,
  hasCareerPage
}): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );

  const industryLabel = (() => {
    const normalized = normalizeIndustry(industry, industries);
    const label = getIndustryDisplayName(normalized ?? industry);
    return label || t('Not specified');
  })();

  const getCompanySize = (sizeId?: number) => {
    if (!sizeId) return t('Not specified');
    const found = companySizeChoices.find((s) => s.value === sizeId);
    return found ? found.to_show : t('Not specified');
  };

  const handleViewCareerPage = () => {
    navigate(`/companies/${slug || id}`);
  };

  return (
    <div className="company-card">
      <div className="company-card__header">
        {logo ? (
          <div
            className="company-card__hero"
            style={{ backgroundImage: `url(${logo})` }}
          />
        ) : (
          <div className="company-card__hero company-card__hero--default" />
        )}
        <div className="company-card__logo-container">
          <img src={logo} alt={name} className="company-card__logo" />
        </div>
      </div>
      <div className="company-card__content">
        <div className="company-card__header-content">
          <h3 className="company-card__name">{name}</h3>
          {hasCareerPage && (
            <div className="company-card__actions">
              <Button
                className="company-card__button"
                onClick={handleViewCareerPage}
                title="Check Career Page"
                t={t}
              />
            </div>
          )}
        </div>
        <div className="company-card__details">
          <div className="company-card__detail-item">
            <div className="company-card__detail-icon">
              <Image name="portfolio.svg" width={14} height={14} />
            </div>
            <div className="company-card__detail-text">
              <span className="company-card__detail-label">
                {t('Industry')}
              </span>
              <span className="company-card__detail-value">
                {industryLabel}
              </span>
            </div>
          </div>

          <div className="company-card__detail-item">
            <div className="company-card__detail-icon">
              <Image name="size.svg" width={16} height={16} />
            </div>
            <div className="company-card__detail-text">
              <span className="company-card__detail-label">{t('Size')}</span>
              <span className="company-card__detail-value">
                {getCompanySize(size)}
              </span>
            </div>
          </div>

          {location && (
            <div className="company-card__detail-item">
              <div className="company-card__detail-icon">
                <Image name="location.svg" width={14} height={14} />
              </div>
              <div className="company-card__detail-text">
                <span className="company-card__detail-label">
                  {t('Location')}
                </span>
                <span className="company-card__detail-value">{location}</span>
              </div>
            </div>
          )}

          {number_of_jobs !== undefined && (
            <div className="company-card__detail-item">
              <div className="company-card__detail-icon">
                <Image name="briefcase.svg" width={18} height={18} />
              </div>
              <div className="company-card__detail-text">
                <span className="company-card__detail-label">
                  {t('Open Jobs')}
                </span>
                <span className="company-card__detail-value">
                  {number_of_jobs}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyCard;
